// The Web Audio API heavily uses the pattern of setting properties directly
// and does not expose any setters to use as an alternative
/* eslint-disable no-param-reassign */
import * as R from 'ramda';
import React from 'react';
import {
  Box,
  Input,
  InputAdornment,
  MenuItem,
  Select,
  Slider,
} from '@material-ui/core';
import { Speed, VolumeDown, VolumeUp } from '@material-ui/icons';
import { useTranslation } from 'react-i18next';

import { SoundProvider } from 'services/Audio/SoundProviders';
import { audioContext as AC } from './common';

// Assigning to a local constant tells TS that the value can't change
// Otherwise checking for null will not eliminate the null type from callbacks
const audioContext = AC;

/*
 * Types
 */
interface SoundConfigBase {
  rate: number;
  volume: number;
  repeat: number;
}
interface SoundConfigFromLib extends SoundConfigBase {
  id: string;
}
interface SoundConfigByURL extends SoundConfigBase {
  url: string;
}
type SoundConfig = SoundConfigFromLib | SoundConfigByURL;

/*
 * Util methods
 */
const loadUrlAsAudioBuffer = (url: string): Promise<AudioBuffer> =>
  new Promise<AudioBuffer>(resolve => {
    if (!audioContext) {
      resolve();
      return;
    }

    const request = new window.XMLHttpRequest();
    request.open('GET', url, true);
    request.responseType = 'arraybuffer';

    request.onload = () =>
      resolve(audioContext.decodeAudioData(request.response));

    request.send();
  });

/*
 * Internal state
 */
const soundLibrary = {
  beepNotOK: loadUrlAsAudioBuffer(
    'data:audio/mp3;base64,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',
  ),
  beepOK: loadUrlAsAudioBuffer(
    'data:audio/mp3;base64,//tQxAADwAABpAAAACAAADSCgAAEAAAMEA8DWiAFMPYDkqQO218B4gBgp1gMIQDE4d6aagtwBUHgYPBXsikTgGNAsBkoRAYYHAGPAZ8nDNzdQHAkEAKLwGgEBhcegYxD35oaJ3gZkGQG6jkBgkYgiBQg9L//QQ3p//+tMzKQ4BYCfLhaIuYf/f3fwAg+K8JwWT5FABg+KcJwZL////80KidL93////mAJYqlUaTSMjkqiNQ0IxTSXYEiaLQ8TT99kAKWNDzztSxHAxlUTg7DcP/7UsRdgBJ1gN5ZSoACYLMsdzFCAgEyECZNAplw+gAoYIIM2HrkVFxk2mV0ExBQhgy4yhAS+bk2NQ3ZM+w3BSYswZ4Z81LJvzc3VaOgP8FlBiaq/qNN1TpqtJL//3e91kyGRyFJ41P//db1u2hKxVK5sWifPniLikyS//7N/1umpv/UgcJxbgCQAAAQgpS2lxcVMopU6hs6JOl4xLR+iEg3RWQbqgYGDIGCDUB+p9gYsB4GEgaGgkWQHULKIMVlsYJMcLxsfsglQSd7NWpJJ0ik//tSxCWAEpmXS5z6gAGKMqq9wdJYcSUOoPiAJFwGDwuI1NVG1P7Wb+7Jpu6m28uJPRI0aIKCYgx90Vn2bZFfo/3+/yZTRokNBoBR30df1f//1K6kiL6ki8GWydGwvkWzL3pcACTAMNHV6FQErXP8XDS0SrX0zanAVK2jWccoSkEc3AJWD3ykYiHMRwD4L9OPwO0ll59TdhcwLTySZ//61VNtvt/ONtGoGVb3///X+3yluikHwDaR63r1f///1l74shV7f+wAuMBBBffpIkqDlKP/+1LEB4AL3ZVZrYoy2X2yq32wtlhB8SmxI7v+vRyXCRTVNWmYaEAWdo4Iixa862h68EyFYYNmdS5oL6vMA0cCeN1P/9/v7f/t4vBNf6+hU3/X9f531C1Gn//2/X+3xstbDGbn4RmXl1AAE3AokmpNdkqsf3F6Xds4/11wNDLAmW1aCCSUSMZh25TOhtYsOHEqmGBzy+rUtQG0BoI+/9fXvqr7/1/D4L/9far/7ffVj50jEBtiJdet6tev/7/+ol98HI9ylUZWhlMABVAMJL5Ffv/7UsQGAAtloVvtFnMRerLtPZG2Ug1tO0CP7sRctfuhYBDlPAs9qhiAyNPb2V3GLZGfbNgchRAd/+v09wUGQLTt//q/tv//iVD1/9///7esm+ozAoMd7L2f/7/39b+smviH75m0REw7AA3UGCObk0Uuq/kE2mJLVZ1zQqIL5bm+j6u1+s4YPoGEd4eXi3MApMigYAFFR67L3O//GgFih//bf6m///Ij/v//+/r/LnmQOghVan/q7LV/99nWMnsmC65iZVeHdmUAH8M7w5NraDce//tSxAcAC72hX8ftsBGDMq8+ntACtw94BUmnXBClM9VgXtbPRbjjRlV07fZT5zOqQXd2ttQfav6vRCiAl6X/6vu22+3/8R3//6P9/9WU2rWYAMYeCfV+2/9X+l6iX2WoDGRd2lTzUM7oABLGQxLt4K4EgEYGGda1AT44yduy2ApHmleg0awHIXDRqmzMvpupM3UXDRk01JppupBBloN/TTdxuf//////sggMyL//////mvTTE/IH/9b/7/0EK3TS3UgHNPy6gEQKAAAAAgAAQAD/+1LEBYALtNllmNkAAWe0H+cUcAAC1Bs+COBIUlYVIDe5jjHgaatsCWiAo5iV+O8c4hpML/lUZohpRJb/y8lJpA2//MTFA+Xjph//mpeMakzIxMP+AhcyFQR/+skBhIAhcyHf/+GAqgAABABBAAAAA498JQgvHRA+EI08mD1vxSEIOTP85QiEYv/4qQkEI1T/8CIijARCMeKv/8eLgtFJY5CQCf//xFLPNAUIxcyv///ocpQ71NOCERTP////ypqCmkxBTUUzLjk5LjWqqqqqqv/7UsQHg8AAAaQcAAAgAAA0gAAABKqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqTEFNRTMuOTkuNaqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqq//tSxGWDwAABpAAAACAAADSAAAAEqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqpMQU1FMy45OS41qqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqr/+1LEoYPAAAGkAAAAIAAANIAAAASqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqkxBTUUzLjk5LjWqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqv/7UsShg8AAAaQAAAAgAAA0gAAABKqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqTEFNRTMuOTkuNaqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqq//tSxKGDwAABpAAAACAAADSAAAAEqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqpMQU1FMy45OS41qqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqr/+1LEoYPAAAGkAAAAIAAANIAAAASqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqkxBTUUzLjk5LjWqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqv/7UsShg8AAAaQAAAAgAAA0gAAABKqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqq//tSxKGDwAABpAAAACAAADSAAAAEqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqo=',
  ),
};
const soundCache: Record<string, Promise<AudioBuffer>> = {};

/*
 * Exports
 */
const playSound = (conf: SoundConfig) => {
  if (!audioContext) return;

  // volume control
  const gain = audioContext.createGain();
  gain.gain.value = conf.volume ?? 1;
  gain.connect(audioContext.destination);

  // Create sources with rate
  const sources = Array(conf.repeat ?? 1)
    .fill(0)
    .map(() => {
      const src = audioContext.createBufferSource();
      src.playbackRate.value = conf.rate ?? 1;
      src.connect(gain);
      return src;
    });

  // Chain sources
  sources.forEach((src, i, arr) => {
    if (arr[i + 1]) {
      src.onended = () => arr[i + 1].start();
    }
  });

  const isUrlSound = (conf: SoundConfig): conf is SoundConfigByURL =>
    !!(conf as any)?.url;

  const audioBufferPromise = isUrlSound(conf)
    ? soundCache[conf.url]
    : soundLibrary[conf.id];

  audioBufferPromise.then(audioBuffer => {
    // Attach buffer to sources
    sources.forEach(src => {
      src.buffer = audioBuffer;
    });
    sources[0].start();
  });
};

/**
 * Sound input component that allows selecting a sound from the internal library
 * and configure its .rate, .volume, and .repeat
 */
const SoundInput = ({ value: v, setValue }) => {
  const value = R.mergeDeepLeft(v, {
    id: Object.keys(soundLibrary)[0],
    volume: 1,
    rate: 1,
    repeat: 1,
  });
  const { t } = useTranslation('audio');
  const changeFnEvt = (prop: string) => e =>
    setValue(R.assoc(prop, e.target.value, value));
  const changeFnVal = (prop: string) => (e, v) =>
    setValue(R.assoc(prop, v, value));

  return (
    <>
      <Box display="flex" width="100%">
        <Box flexGrow={1} flexBasis={0}>
          <Select
            type="option"
            value={value.id ?? '-'}
            onChange={changeFnEvt('id')}
            style={{ width: '100%' }}
          >
            <MenuItem value="-">-</MenuItem>
            {Object.keys(soundLibrary).map(id => (
              <MenuItem value={id}>
                {t(`config.providers.libSound.library.${id}`)}
              </MenuItem>
            ))}
          </Select>
        </Box>

        <Box flexGrow={1} flexBasis={0} display={value.id ? undefined : 'none'}>
          <Input
            startAdornment={
              <InputAdornment position="start">
                {t('config.providers.libSound.inputs.repeat', {
                  context: 'start',
                })}
              </InputAdornment>
            }
            endAdornment={
              <InputAdornment position="end">
                {t('config.providers.libSound.inputs.repeat', {
                  context: 'end',
                })}
              </InputAdornment>
            }
            type="number"
            inputProps={{ min: 1, max: 5, step: 1 }}
            value={value.repeat}
            onChange={e =>
              setValue(R.assoc('repeat', Number(e.target.value), value))
            }
          />
        </Box>
      </Box>

      <Box
        display={value.id ? 'flex' : 'none'}
        alignItems="center"
        margin="1em"
      >
        <VolumeDown />
        <Box flexGrow={1}>
          <Slider
            valueLabelFormat={`${100 * value.volume}%`}
            valueLabelDisplay="auto"
            value={value.volume}
            onChange={changeFnVal('volume')}
            min={0}
            max={2}
            step={0.05}
          />
        </Box>
        <VolumeUp />

        <Speed />
        <Box flexGrow={1}>
          <Slider
            valueLabelFormat={`${value.rate.toFixed(2)}x`}
            valueLabelDisplay="auto"
            value={value.rate}
            onChange={changeFnVal('rate')}
            min={0.5}
            max={4}
            step={0.25}
          />
        </Box>
        <Speed />
      </Box>
    </>
  );
};

/**
 * libSound allows the user to play back sounds by URL or from a built in library
 *
 * For each sound, the user can configure the volume, the playback rate (which also affects the pitch), and number of repeats
 */
const libSound: SoundProvider<SoundConfig> = {
  id: 'libSound',
  playSound,
  SoundInput,
  onConfigUpdated: conf => {
    if (!audioContext) return;
    if ('url' in conf) {
      soundCache[conf.url] = loadUrlAsAudioBuffer(conf.url);
    }
  },
};
export default libSound;
